import { some } from 'lodash/fp';
import { alias, from, group, identifier, select, sql } from 'app/lib/utils/sql_generator.js';
import {
    translateDoubleQuoteString,
    translateFunction,
    translateLiteral,
    translateOp,
    translateParens,
    translateSheetReference,
    translateSimpleReference,
    translateTernaryOp
} from './helpers.js';
import { getFormulaTree } from './utils.js';

export class Converter {
    #helpers;

    constructor(columns = [], sources = []) {
        this.columns = columns;
        this.sources = sources;

        this.#helpers = {
            SimpleReference: translateSimpleReference(this.columns),
            SheetReference: translateSheetReference(this.sources),
            Function: translateFunction(this),
            TernaryExpression: translateTernaryOp(this),
            Int: translateLiteral,
            Double: translateLiteral,
            SingleQuoteString: translateLiteral,
            DoubleQuoteString: translateDoubleQuoteString,
            AdditiveOp: translateOp,
            EqualityOp: translateOp,
            RelationalOp: translateOp,
            MultiplicativeOp: translateOp,
            RP: translateParens,
            LP: translateParens
        };
    }

    translateNode(node, buffer) {
        const helper = this.#helpers[node.key];

        if (node.key === '⚠') {
            throw Error('syntax error');
        } else if (helper) {
            buffer.push(helper(node));
        } else if (node.children.length) {
            for (const child of node.children) {
                this.translateNode(child, buffer);
            }
        }
    }

    translate(node) {
        const buffer = [];

        if (!node) {
            throw Error('null node');
        }

        this.translateNode(node, buffer);

        const tableExp = buffer.find((item) => item?.tableExp)?.tableExp;
        const type = buffer.find((item) => item?.type)?.type;
        const group = buffer.find((item) => item.group)?.group;
        const agg = buffer.find((item) => item.agg)?.agg;

        return {
            exp: buffer.map((item) => item?.exp).join(''),
            tableExp,
            type,
            group,
            agg
        };
    }

    toSql(formula) {
        const tree = getFormulaTree(formula);

        let sql;

        try {
            sql = this.translate(tree);
        } catch (e) {
            console.warn('Error translating formula:', e, formula);
            sql = { exp: `-- ${formula}\nNULL` };
        }

        return sql;
    }
}

const stripSource = (expression) => expression?.replaceAll('Source.', '');

const wrapAnyValue = (expression) => `ANY_VALUE(${expression})`;

export function buildSqlFromExpressions(name, expressions, columnNames) {
    const isGrouped = some((expression) => expression.group, expressions);
    const tableExp = stripSource(expressions.find((expression) => expression.tableExp)?.tableExp);
    const columns = [];

    if (isGrouped) {
        expressions.forEach((expression) => {
            if (!expression.agg && !expression.group && expression.exp.includes('Source.')) {
                expression.exp = wrapAnyValue(expression.exp);
            }
        });
    }

    for (let i = 0; i < columnNames.length; i++) {
        const columnName = columnNames[i];
        const expression = expressions[i];

        columns.push(alias(stripSource(expression.exp), identifier(columnName)));
    }

    const groups = expressions.map((e, i) => (e.group ? i + 1 : null));

    return sql(select(columns), from(identifier(name), tableExp), group(...groups));
}
